/* eslint-disable no-console */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import {
  filterRespondItemsByMitra,
  getLastOwnEditableResponse,
  mapResponsesByClauseId,
  normalizeRespondClauseDetail,
  sortRespondClauseDetails,
  stampSavedResponse,
  toClauseResponseList,
  unwrapRespondListItems,
} from './helpers';
import { buildRespondClauseListUrl } from './listParams';
import { lockRespondStatus, LOCK_RESOURCE_CLAUSE } from './lockRespondStatus';
import { unwrapPayload } from '../../api/unwrap';

const parseResponseItems = payload => {
  const list = unwrapRespondListItems(payload)
    .map(normalizeRespondClauseDetail)
    .filter(Boolean);

  if (list.length > 0) return list;

  const single = normalizeRespondClauseDetail(unwrapPayload(payload));
  return single ? [single] : [];
};

const upsertClauseResponseList = (list, saved) =>
  sortRespondClauseDetails([
    ...toClauseResponseList(list).filter(
      item => String(item.id) !== String(saved.id)
    ),
    saved,
  ]);

const useClarificationClauseResponses = (
  authToken = null,
  procurementMitraId = null,
  procurementClauseDetailIds = [],
  isEvaluatorView = true,
  procurementId = null
) => {
  const [clauseResponses, setClauseResponses] = useState({});
  const [isLoading, setIsLoading] = useState(
    Boolean(
      procurementId && procurementMitraId && procurementClauseDetailIds?.length
    )
  );
  const [isSaving, setIsSaving] = useState(false);
  const fetchSeqRef = useRef(0);

  const clauseDetailIdsKey = useMemo(
    () =>
      [...new Set((procurementClauseDetailIds ?? []).filter(Boolean))]
        .sort()
        .join(','),
    [procurementClauseDetailIds]
  );

  const clauseDetailIds = useMemo(() => {
    if (!clauseDetailIdsKey) return [];
    return clauseDetailIdsKey.split(',').filter(Boolean);
  }, [clauseDetailIdsKey]);

  const fetchResponses = useCallback(async () => {
    const token = authToken || getAuthToken();
    if (
      !token ||
      !procurementId ||
      !procurementMitraId ||
      clauseDetailIds.length === 0
    ) {
      setClauseResponses({});
      setIsLoading(false);
      return;
    }

    const seq = fetchSeqRef.current + 1;
    fetchSeqRef.current = seq;
    setIsLoading(true);
    try {
      const response = await axios.get(
        buildRespondClauseListUrl({
          procurementId,
          procurementMitraId,
        }),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        }
      );

      if (fetchSeqRef.current !== seq) return;

      const clauseIdSet = new Set(clauseDetailIds.map(String));
      const items = filterRespondItemsByMitra(
        parseResponseItems(response),
        procurementMitraId,
        { scopedQuery: true }
      ).filter(item => clauseIdSet.has(String(item.procurementClauseDetailId)));

      setClauseResponses(mapResponsesByClauseId(items));
    } catch (error) {
      if (fetchSeqRef.current !== seq) return;
      if (error?.response?.status === 404) {
        setClauseResponses({});
        return;
      }
      console.error('Error fetching clause responses:', error);
    } finally {
      if (fetchSeqRef.current === seq) setIsLoading(false);
    }
  }, [authToken, clauseDetailIds, procurementId, procurementMitraId]);

  useEffect(() => {
    fetchResponses();
  }, [fetchResponses]);

  const saveResponse = useCallback(
    async (procurementClauseDetailId, respondNotes) => {
      const token = authToken || getAuthToken();
      if (!token || !procurementMitraId || !procurementClauseDetailId) {
        return null;
      }

      const existing = getLastOwnEditableResponse(
        clauseResponses[procurementClauseDetailId],
        { isEvaluator: isEvaluatorView }
      );
      const payload = {
        ...(isEvaluatorView ? { procurementMitraId } : {}),
        procurementClauseDetailId,
        respondNotes,
      };

      setIsSaving(true);
      try {
        const response = existing?.id
          ? await axios.put(
              `${api.API_URL}/respond-clause-detail/${existing.id}`,
              payload,
              {
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
              }
            )
          : await axios.post(`${api.API_URL}/respond-clause-detail`, payload, {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
            });

        const saved = stampSavedResponse(
          normalizeRespondClauseDetail(unwrapPayload(response)),
          { isEvaluator: isEvaluatorView }
        );

        if (saved) {
          setClauseResponses(prev => ({
            ...prev,
            [procurementClauseDetailId]: upsertClauseResponseList(
              prev[procurementClauseDetailId],
              saved
            ),
          }));
          return saved;
        }

        await fetchResponses();
        return null;
      } catch (error) {
        if (error?.response?.status === 409) {
          await fetchResponses();
        }
        console.error('Error saving clause response:', error);
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [
      authToken,
      clauseResponses,
      fetchResponses,
      isEvaluatorView,
      procurementMitraId,
    ]
  );

  const deleteResponse = useCallback(
    async procurementClauseDetailId => {
      const token = authToken || getAuthToken();
      const existing = getLastOwnEditableResponse(
        clauseResponses[procurementClauseDetailId],
        { isEvaluator: isEvaluatorView }
      );

      if (!token || !existing?.id) {
        return;
      }

      setIsSaving(true);
      try {
        await axios.delete(
          `${api.API_URL}/respond-clause-detail/${existing.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setClauseResponses(prev => {
          const next = { ...prev };
          const remaining = toClauseResponseList(
            prev[procurementClauseDetailId]
          ).filter(item => String(item.id) !== String(existing.id));

          if (remaining.length) {
            next[procurementClauseDetailId] = remaining;
          } else {
            delete next[procurementClauseDetailId];
          }

          return next;
        });
      } catch (error) {
        if (error?.response?.status === 409) {
          await fetchResponses();
        }
        console.error('Error deleting clause response:', error);
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [authToken, clauseResponses, fetchResponses, isEvaluatorView]
  );

  const lockResponse = useCallback(
    async (responseId, isLocked = true, finalValue = null) => {
      const token = authToken || getAuthToken();
      if (!token || !responseId) return;
      const nextFinalValue = isLocked === true ? (finalValue ?? null) : null;

      setIsSaving(true);
      try {
        await lockRespondStatus({
          token,
          resource: LOCK_RESOURCE_CLAUSE,
          id: responseId,
          isLocked,
          finalValue: nextFinalValue,
        });
        setClauseResponses(prev => {
          const next = { ...prev };
          Object.keys(next).forEach(clauseId => {
            next[clauseId] = toClauseResponseList(next[clauseId]).map(item =>
              String(item.id) === String(responseId)
                ? {
                    ...item,
                    isLocked: isLocked === true,
                    finalValue: nextFinalValue,
                  }
                : item
            );
          });
          return next;
        });
      } catch (error) {
        console.error('Error locking clause response:', error);
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [authToken]
  );

  return {
    clauseResponses,
    isLoading,
    isSaving,
    refresh: fetchResponses,
    saveResponse,
    deleteResponse,
    lockResponse,
  };
};

export default useClarificationClauseResponses;
